import { useState, useMemo, forwardRef } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "cmdk";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Combobox — Radix Popover + cmdk asosida searchable single-select.
 * Faza 3 use-case'lar:
 *   - Pipeline tanlash, Stage tanlash, Assignee tanlash, Contact tanlash
 *
 * Accessibility:
 *   - cmdk a11y (combobox role, listbox, aria-selected)
 *   - Keyboard: ↑↓ navigate, Enter select, Esc close, type-to-search
 */
export interface ComboboxOption<T = unknown> {
  value: string;
  label: string;
  data?: T;
}

export interface ComboboxProps<T = unknown> {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  options: ComboboxOption<T>[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  /** Trigger ko'rinishini moslashtirish */
  triggerClassName?: string;
  /** Popover panel kengligi (px) */
  width?: number;
  renderOption?: (option: ComboboxOption<T>) => React.ReactNode;
  renderSelected?: (option: ComboboxOption<T>) => React.ReactNode;
  invalid?: boolean;
  id?: string;
  "aria-label"?: string;
}

export const Combobox = forwardRef<HTMLButtonElement, ComboboxProps>(function Combobox(
  {
    value,
    onChange,
    options,
    placeholder = "Tanlang...",
    searchPlaceholder = "Qidirish...",
    emptyText = "Topilmadi",
    disabled,
    clearable = false,
    className,
    triggerClassName,
    width = 280,
    renderOption,
    renderSelected,
    invalid,
    id,
    "aria-label": ariaLabel,
  },
  ref,
) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverPrimitive.Trigger asChild>
        <button
          ref={ref}
          id={id}
          type="button"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn(
            "inline-flex h-10 w-full items-center justify-between gap-2 rounded-lg border px-3 text-sm",
            "bg-[var(--form-field-bg)] border-[var(--form-field-border)] text-[var(--text-primary)]",
            "transition-colors duration-150",
            "hover:border-[var(--form-field-border-hover)]",
            "focus:border-[var(--form-field-border-focus)] focus:outline-none focus:ring-2 focus:ring-[var(--form-field-ring-focus)]",
            "disabled:cursor-not-allowed disabled:opacity-50",
            invalid &&
              "border-[var(--form-error-border)] focus:border-[var(--form-error-border-focus)] focus:ring-[var(--form-error-ring)]",
            triggerClassName,
            className,
          )}
        >
          <span className={cn("truncate", !selected && "text-[var(--form-field-placeholder)]")}>
            {selected ? renderSelected?.(selected) ?? selected.label : placeholder}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            {clearable && selected && !disabled && (
              <span
                role="button"
                aria-label="Tozalash"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
                className="inline-flex h-5 w-5 items-center justify-center rounded hover:bg-[var(--bg-surface)]"
              >
                <X className="h-3 w-3 text-[var(--text-muted)]" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 text-[var(--text-muted)]" aria-hidden />
          </span>
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className={cn(
            "z-[var(--z-popover,60)] rounded-lg border border-[var(--border-strong)] bg-[var(--bg-elevated)]",
            "shadow-[var(--shadow-modal)] overflow-hidden",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          )}
          style={{ width }}
        >
          <Command
            className="flex w-full flex-col"
            filter={(value, search) => {
              if (!search) return 1;
              return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
            }}
          >
            <div className="border-b border-[var(--border-subtle)] px-2 py-1.5">
              <CommandInput
                placeholder={searchPlaceholder}
                className="h-8 w-full bg-transparent px-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none"
              />
            </div>
            <CommandList className="max-h-[260px] overflow-y-auto p-1">
              <CommandEmpty className="py-6 text-center text-sm text-[var(--text-muted)]">
                {emptyText}
              </CommandEmpty>
              <CommandGroup>
                {options.map((opt) => {
                  const isSelected = opt.value === value;
                  return (
                    <CommandItem
                      key={opt.value}
                      value={`${opt.label} ${opt.value}`}
                      onSelect={() => {
                        onChange(opt.value);
                        setOpen(false);
                      }}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm",
                        "text-[var(--text-secondary)] aria-selected:bg-[var(--bg-surface)] aria-selected:text-[var(--text-primary)]",
                      )}
                    >
                      <Check
                        className={cn(
                          "h-4 w-4 shrink-0 text-[var(--accent-violet)]",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                        aria-hidden
                      />
                      <span className="flex-1 truncate">
                        {renderOption?.(opt) ?? opt.label}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}) as <T = unknown>(p: ComboboxProps<T> & { ref?: React.Ref<HTMLButtonElement> }) => React.ReactElement;
